import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Check, GitCompareArrows, Pencil, Plus, Send, Trash2, X } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomeCadastro, nomeParceiro, numero, textoLivre } from '../../nucleo/formato';
import { useSessaoFluxo } from './AvisoModo';
import { Comparativo } from './Comparativo';
import { Linha, Secao } from './comum';
import { DetalheCotacao } from './DetalheCotacao';
import { FormCotacao } from './FormCotacao';
import { FormRequisicao } from './FormRequisicao';
import { COR_PEDIDO, COR_REQUISICAO, invalidarCompras, qtd, type Cotacao, type Pagina, type RequisicaoDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { AreaSensivel } from '../../componentes/formulario';
import { useCamposDaTela } from '../../nucleo/campos-tela';

/** Ficha da requisição (M10.1): itens, cotações com comparativo, aprovação ou recusa e conversão da cotação escolhida em pedido. */
export function DetalheRequisicao({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  // Lote de 19/10 (item 9): o campo escondido em Configurações > Campos das telas some do detalhe (o dado continua).
  const tela = useCamposDaTela('compras.requisicao');
  const q = useQuery({ queryKey: ['/compras/requisicoes', 'obter', id], queryFn: () => get<RequisicaoDetalhe>(`/compras/requisicoes/${id}`), enabled: !!id });
  const d = q.data;
  const cotacoes = useQuery({ queryKey: ['/compras/cotacoes', 'da-requisicao', id], queryFn: () => get<Pagina<Cotacao>>('/compras/cotacoes', { requisicao_id: id, limite: 50 }), enabled: !!id });
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cotando, setCotando] = useState(false);
  const [comparando, setComparando] = useState(false);
  const [cotacaoAberta, setCotacaoAberta] = useState<string | null>(null);
  const [pedidoId] = useState(() => crypto.randomUUID());

  const acao = (caminho: string, sucesso: string, corpo?: unknown) => ({
    mutationFn: () => post(caminho, corpo),
    onSuccess: async () => {
      await invalidarCompras(qc);
      toast.sucesso(sucesso);
    },
    onError: (e: unknown) => toast.erro(mensagemDeErro(e)),
  });
  const enviar = useMutation(acao(`/compras/requisicoes/${id}/enviar`, t('mensagens.requisicaoEnviada')));
  const aprovar = useMutation(acao(`/compras/requisicoes/${id}/aprovar`, t('mensagens.requisicaoAprovada')));
  const recusar = useMutation({
    mutationFn: () => post(`/compras/requisicoes/${id}/recusar`, { motivo: motivo.trim() }),
    onSuccess: async () => {
      await invalidarCompras(qc);
      setRecusando(false);
      setMotivo('');
      toast.sucesso(t('mensagens.requisicaoRecusada'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const excluir = useMutation({
    mutationFn: () => del(`/compras/requisicoes/${id}`),
    onSuccess: async () => {
      await invalidarCompras(qc);
      setExcluindo(false);
      toast.sucesso(tn('comum.removido'));
      aoFechar();
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const escolhida = cotacoes.data?.itens.find((c) => c.escolhida) ?? null;
  const converter = useMutation({
    mutationFn: () => post('/compras/pedidos/de-cotacao', { id: pedidoId, cotacao_id: escolhida!.id }),
    onSuccess: async () => {
      await invalidarCompras(qc);
      toast.sucesso(t('mensagens.pedidoCriado'));
      aoFechar();
      navegar(`/operacao/compras/pedidos?pedido=${pedidoId}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const estado = d?.estado;
  const rodape = !d ? undefined : (
    <>
      {estado === 'rascunho' && s.pode('compras.excluir') && (
        <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
          {tn('comum.excluir')}
        </Botao>
      )}
      {estado === 'rascunho' && s.pode('compras.editar') && (
        <Botao icone={<Pencil className="size-4" />} onClick={() => setEditando(true)}>
          {tn('comum.editar')}
        </Botao>
      )}
      {/* Val5 (29/10): enviar (e confirmar) tem ação própria (`compras.enviar`). */}
      {estado === 'rascunho' && s.pode('compras.enviar') && (
        <Botao variante="primario" icone={<Send className="size-4" />} carregando={enviar.isPending} onClick={() => enviar.mutate()}>
          {t('acoes.enviarRequisicao')}
        </Botao>
      )}
      {estado === 'enviada' && s.pode('compras.aprovar') && (
        <Botao variante="perigo" icone={<X className="size-4" />} onClick={() => setRecusando(true)}>
          {t('acoes.recusar')}
        </Botao>
      )}
      {estado === 'enviada' && s.pode('compras.aprovar') && (
        <Botao variante="primario" icone={<Check className="size-4" />} carregando={aprovar.isPending} onClick={() => aprovar.mutate()}>
          {t('acoes.aprovar')}
        </Botao>
      )}
      {estado === 'aprovada' && s.pode('compras.criar') && (
        <Botao variante="primario" icone={<ArrowRightLeft className="size-4" />} disabled={!escolhida} carregando={converter.isPending} title={escolhida ? undefined : t('requisicao.escolhaPrimeiro')} onClick={() => converter.mutate()}>
          {t('acoes.converterEmPedido')}
        </Botao>
      )}
    </>
  );

  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={d ? t('requisicao.titulo', { numero: d.numero }) : t('requisicao.tituloCurto')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {rodape}
          </>
        }
      >
        {q.isPending && !!id && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {d && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Selo cor={COR_REQUISICAO[d.estado]}>{t(`estadoRequisicao.${d.estado}`)}</Selo>
            </div>
            <p className="mb-4 text-sm text-tinta-1">{textoLivre(d.justificativa)}</p>
            {d.estado === 'recusada' && d.motivo_recusa && (
              <p role="status" className="mb-4 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                {t('requisicao.recusadaPor', { motivo: d.motivo_recusa })}
              </p>
            )}
            <dl className="divide-y divide-borda">
              {tela.visivel('solicitante_nome') && <Linha rotulo={t('campos.solicitante')}>{textoLivre(d.solicitante_nome)}</Linha>}
              <Linha rotulo={t('campos.criadoEm')}>{data(d.created_at)}</Linha>
              {d.decidido_em && <Linha rotulo={t('campos.decididoEm')}>{data(d.decidido_em)}</Linha>}
            </dl>

            <Secao titulo={t('secoes.itens')}>
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.itens_detalhe.map((i) => (
                  <li key={i.produto_id} className="flex min-w-0 items-start justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate" title={i.produto_nome ?? undefined}>{nomeCadastro(i.produto_nome)}</span>
                      <span className="numero block truncate text-xs text-tinta-3" title={i.produto_codigo ?? undefined}>{i.produto_codigo}</span>
                      {i.observacao && <span className="block truncate text-xs text-tinta-3" title={textoLivre(i.observacao)}>{textoLivre(i.observacao)}</span>}
                    </span>
                    <span className="numero shrink-0">
                      {qtd(i.quantidade, numero)} {i.unidade ?? ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Secao>

            <Secao
              titulo={t('secoes.cotacoes')}
              acao={
                <span className="flex items-center gap-2">
                  {(cotacoes.data?.itens.length ?? 0) > 0 && (
                    <Botao tamanho="sm" icone={<GitCompareArrows className="size-4" />} onClick={() => setComparando(true)}>
                      {t('acoes.comparar')}
                    </Botao>
                  )}
                  {(d.estado === 'enviada' || d.estado === 'aprovada') && s.pode('compras.criar') && (
                    <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setCotando(true)}>
                      {t('acoes.novaCotacao')}
                    </Botao>
                  )}
                </span>
              }
            >
              {cotacoes.isPending && <Carregando />}
              {cotacoes.isError && <Erro erro={cotacoes.error} aoTentar={() => cotacoes.refetch()} />}
              {cotacoes.data && cotacoes.data.itens.length === 0 && <p className="text-sm text-tinta-3">{d.estado === 'rascunho' ? t('requisicao.cotarDepoisDeEnviar') : t('requisicao.semCotacoes')}</p>}
              {cotacoes.data && cotacoes.data.itens.length > 0 && (
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {cotacoes.data.itens.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => setCotacaoAberta(c.id)} className="flex w-full min-w-0 items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-sup-2">
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-medium" title={nomeParceiro(c.fornecedor_nome)}>{nomeParceiro(c.fornecedor_nome)}</span>
                          <TextoCortado className="text-xs text-tinta-3">{c.prazo_maximo_dias === null ? '' : t('comparativo.prazoDias', { count: c.prazo_maximo_dias })}</TextoCortado>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {c.escolhida && <Selo cor="verde">{t('cotacao.escolhida')}</Selo>}
                          {c.vencida && <Selo cor="laranja">{t('cotacao.vencida')}</Selo>}
                          <span className="numero">{anunciarOcultos(dinheiro(c.total_centavos, c.moeda))}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Secao>

            {d.pedidos_ligados.length > 0 && (
              <Secao titulo={t('secoes.pedidosGerados')}>
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {d.pedidos_ligados.map((p) => (
                    <li key={p.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
                      <Link to={`/operacao/compras/pedidos?pedido=${p.id}`} className="text-acento-forte hover:underline" onClick={aoFechar}>
                        <TextoCortado className="block">
                          {p.numero}
                        </TextoCortado>
                      </Link>
                      <Selo cor={COR_PEDIDO[p.estado]}>{t(`estadoPedido.${p.estado}`)}</Selo>
                    </li>
                  ))}
                </ul>
              </Secao>
            )}

            <div className="mt-6 border-t border-borda pt-4">
              <Chatter entidade="compras.requisicao" registroId={d.id} titulo={t('requisicao.titulo', { numero: d.numero })} rotulosCampos={{ estado: t('campos.estado'), justificativa: t('campos.justificativa'), motivo_recusa: t('campos.motivoRecusa') }} />
            </div>
          </>
        )}
      </Drawer>

      <FormRequisicao aberto={editando && !!d} requisicao={d ?? null} aoFechar={() => setEditando(false)} />
      <FormCotacao aberto={cotando && !!d} requisicaoId={d?.id} aoFechar={() => setCotando(false)} aoSalvar={(cid) => setCotacaoAberta(cid)} />
      <Comparativo requisicaoId={comparando ? (id ?? null) : null} aoFechar={() => setComparando(false)} />
      <DetalheCotacao id={cotacaoAberta} aoFechar={() => setCotacaoAberta(null)} />
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('requisicao.excluirTitulo')} texto={t('requisicao.excluirTexto')} />
      <Modal
        aberto={recusando}
        aoFechar={() => setRecusando(false)}
        titulo={t('requisicao.recusarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(false)}>
              {tn('comum.voltar')}
            </Botao>
            <Botao variante="perigo" disabled={!motivo.trim()} carregando={recusar.isPending} onClick={() => recusar.mutate()}>
              {t('acoes.recusar')}
            </Botao>
          </>
        }
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('campos.motivoRecusa')}
          <AreaSensivel rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
      </Modal>
    </>
  );
}
